import React, { useState, useEffect } from "react";
import { inject, observer } from "mobx-react";
import { Modal, Typography } from "antd";
import { checkIsEnglish, compileMessageForLength, caculateMessageLengthWithLineBreaks, checkTotalMessageCount } from "common/utils/messageUtilities";
import { replaceShortUrlPlaceholderForLength, getShortUrlMarkerSamples } from "common/utils/brandedLinks";
import { langRegEx, optOutMessageSample, replyStopMessageSample, replyLinkMessageSample, unicodeCharArr } from "constant/messageConstants";
import AppButton from "components/AppButton/AppButton";
import UnicodeDetector from "components/GatewayMessage/Unicode/UnicodeDetector";
import SMSCounter from "./SmsCounter";
import styles from "assets/css/modals.module.css";

const CharactersCounter = props => {
  const { name, isMms, balance, values, contacts, message, modalStore, messageStore, testMessageStore, setFieldValue, setMessage, smallSize = false, authStore } = props;
  const [totalCharacterCount, setTotalCharacterCountstate] = useState(0);
  const [totalMessagesCount, setTotalMessageCountState] = useState(0);
  const [msgLimit, setMsgLimit] = useState(false);

  const unicodeSMSCharLimit = unicodeCharArr?.[unicodeCharArr.length - 1]?.max || "";
  const unicodeSMSLimit = unicodeCharArr?.[unicodeCharArr.length - 1]?.sms || "";

  useEffect(() => {
    const { optout, replyLink, replyStopToOptOut } = values;
    const previewMessage = replaceShortUrlPlaceholderForLength(message, authStore?.user);
    if (optout || replyStopToOptOut || replyLink) {
      messageStore.setCurrentMessage(
        <Typography.Text>
          {previewMessage}
          <br />
          {replyStopToOptOut && replyStopMessageSample}
          {optout && optOutMessageSample}
          {replyLink && replyLinkMessageSample}
        </Typography.Text>
      );
    } else {
      messageStore.setCurrentMessage(<Typography.Text>{previewMessage}</Typography.Text>);
    }
    let tempMessage = compileMessageForLength({
      message,
      optOut: optout,
      replyStopToOptOut,
      replyLink,
      user: authStore?.user
    });

    const characterCount = caculateMessageLengthWithLineBreaks(tempMessage);
    const messageCount = checkTotalMessageCount(characterCount, checkIsEnglish(message));

    setTotalCharacterCountstate(characterCount);
    setTotalMessageCountState(messageCount);

    testMessageStore.updateMessageCounts(characterCount, messageCount);

    if (message && (isMms ? characterCount > 1500 : characterCount > (langRegEx.test(message) ? 1224 : unicodeSMSCharLimit))) {
      setMsgLimit(true);
    }
  }, [message, totalCharacterCount, values, testMessageStore]);

  return (
    <>
      <SMSCounter
        characters={totalCharacterCount || 0}
        sms={totalMessagesCount || 1}
        contacts={contacts}
        credits={balance}
        approx={values.replyStopToOptOut || values.optout || values.replyLink}
        isMms={isMms}
        mobile={smallSize}
      />
      <UnicodeDetector
        isMms={isMms}
        message={message}
        onChange={e => {
          setFieldValue(name, e);
          setMessage(e);
        }}
        textBoxComp={<textarea className={styles.removeUnicodeTextarea} value={message} maxLength={isMms ? 1500 : langRegEx.test(message) ? 1224 : unicodeSMSCharLimit} />}
      />
      <Modal visible={msgLimit} closable={false} footer={null} zIndex={1032}>
        <div className="text-center">
          <h6 className="fw-bold w-75 mx-auto">
            {isMms
              ? "You have exceeded the maximum character limit of 1500"
              : langRegEx.test(message)
              ? "Maximum 8 SMS per contact allowed (1224 characters)"
              : `Maximum ${unicodeSMSLimit} SMS per contact allowed (${unicodeSMSCharLimit} unicode characters)`}
          </h6>
          <span>Edit your message here</span>
          <SMSCounter characters={totalCharacterCount} isMms={true} limit={isMms ? 1500 : langRegEx.test(message) ? 1224 : unicodeSMSCharLimit} />
          <textarea
            className={`border border-info-color px-3 rounded ${styles.characterTextarea}`}
            value={message}
            name={name}
            onChange={e => {
              setFieldValue(name, e.target.value);
              setMessage(e.target.value);
            }}
            onKeyDown={e => {
              if (e.key === "Backspace") {
                const target = e.target;
                const currentValue = target.value;
                let cursorPos = target.selectionStart;

                if (cursorPos === 0) return;

                // Check for short URLs
                const markers = getShortUrlMarkerSamples(authStore?.user);
                for (const shortUrlPattern of markers) {
                  const escapedShortUrl = String(shortUrlPattern).replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
                  const markerRegex = new RegExp(escapedShortUrl, "gi");
                  const shortUrlMatches = [...currentValue.matchAll(markerRegex)];

                  for (let match of shortUrlMatches) {
                    const markerStart = match.index;
                    const markerEnd = markerStart + match[0].length;

                    if (cursorPos > markerStart && cursorPos <= markerEnd) {
                      e.preventDefault();
                      const newValue = currentValue.slice(0, markerStart) + currentValue.slice(markerEnd);
                      target.value = newValue;
                      target.setSelectionRange(markerStart, markerStart);
                      setFieldValue(name, newValue);
                      setMessage(newValue);
                      return;
                    }
                  }
                }

                // Check for merge fields
                const mergeFieldRegex = /{{[^}]+}}/g;
                const mergeFieldMatches = [...currentValue.matchAll(mergeFieldRegex)];

                for (let match of mergeFieldMatches) {
                  const fieldStart = match.index;
                  const fieldEnd = fieldStart + match[0].length;

                  if (cursorPos > fieldStart && cursorPos <= fieldEnd) {
                    e.preventDefault();
                    const newValue = currentValue.slice(0, fieldStart) + currentValue.slice(fieldEnd);
                    target.value = newValue;
                    target.setSelectionRange(fieldStart, fieldStart);
                    setFieldValue(name, newValue);
                    setMessage(newValue);
                    return;
                  }
                }
              }
            }}
          />
          <AppButton
            label="OK"
            light
            className="px-3"
            onClick={() => setMsgLimit(false)}
            disabled={isMms ? totalCharacterCount > 1500 : totalCharacterCount > (langRegEx.test(message) ? 1224 : unicodeSMSCharLimit)}
          />
        </div>
      </Modal>
    </>
  );
};

export default inject(stores => ({
  messageStore: stores.store.messageStore,
  modalStore: stores.store.modalStore,
  testMessageStore: stores.store.testMessageStore,
  authStore: stores.store.authStore
}))(observer(CharactersCounter));
